import { useCallback, useEffect, useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import GuidedCareCard from "./GuidedCareCard";
import aggressionTantrums from "../../assets/areas-we-provide/aggression-tantrums.svg";
import anxiety from "../../assets/areas-we-provide/anxiety.svg";
import attentionAdhd from "../../assets/areas-we-provide/attention-adhd.svg";
import autismSpectrum from "../../assets/areas-we-provide/autism-spectrum.svg";
import behavioralIssues from "../../assets/areas-we-provide/behavioral-issues.svg";
import communicationDelay from "../../assets/areas-we-provide/communication-delay.svg";
import developmentalDelays from "../../assets/areas-we-provide/developmental-delays.svg";
import emotionalRegulation from "../../assets/areas-we-provide/emotional-regulation.svg";
import feedingSleepToileting from "../../assets/areas-we-provide/feeding-sleeping.svg";
import parentingGuidance from "../../assets/areas-we-provide/parenting-guidance.svg";
import socialInteraction from "../../assets/areas-we-provide/social-interaction.svg";

import bgIcon from "../../assets/areas-we-provide/bg-icon.svg";
import { useTranslation } from "react-i18next";
import { useLocal } from "hooks/useLocal";

type SupportArea = {
  imageSrc: string;
  link: string;
  linkText: string;
  title: string;
  whatItLooksLike: string;
  howWeSupport: string;
};

const supportAreas: SupportArea[] = [
  {
    imageSrc: aggressionTantrums,
    link: "#",
    linkText: "Learn more",
    title: "Aggression & Tantrums",
    whatItLooksLike:
      "Your child may have intense tantrums that feel impossible to manage, hitting, yelling, or crumbling after big emotions.",
    howWeSupport:
      "We teach co-regulation strategies, calming scripts, and visual tools so the whole family can navigate big feelings with confidence.",
  },
  {
    imageSrc: anxiety,
    link: "#",
    linkText: "Learn more",
    title: "Anxiety & Depression",
    whatItLooksLike:
      "You might notice avoidance, tummy aches, or low motivation that keeps your child from doing what they love.",
    howWeSupport:
      "Therapists blend CBT techniques with playful exposure plans to help kids name feelings, build coping kits, and reconnect with joy.",
  },
  {
    imageSrc: attentionAdhd,
    link: "#",
    linkText: "Learn more",
    title: "Attention, Focus, ADHD",
    whatItLooksLike:
      "Homework takes hours, directions vanish mid-sentence, and energy feels nonstop.",
    howWeSupport:
      "We create routines, sensory breaks, and parent coaching plans that channel energy into focus and wins your child can feel.",
  },
  {
    imageSrc: autismSpectrum,
    link: "#",
    linkText: "Learn more",
    title: "Autism Spectrum Disorders",
    whatItLooksLike:
      "Maybe transitions are tough, sensory input is overwhelming, or communication looks different.",
    howWeSupport:
      "Our team combines speech, OT, and ABA-informed strategies to build communication, independence, and joyful connection.",
  },
  {
    imageSrc: behavioralIssues,
    link: "#",
    linkText: "Learn more",
    title: "Behavioral Issues",
    whatItLooksLike:
      "Daily routines become power struggles and consequences stop working.",
    howWeSupport:
      "We uncover triggers, set up proactive supports, and coach caregivers so expectations feel clear and doable.",
  },
  {
    imageSrc: communicationDelay,
    link: "#",
    linkText: "Learn more",
    title: "Communication & Speech Delays",
    whatItLooksLike:
      "Words are slow to come, your child gets frustrated, or articulation makes them shy.",
    howWeSupport:
      "Speech therapists use play-based sessions, AAC supports, and home practice plans to grow expressive and receptive language.",
  },
  {
    imageSrc: developmentalDelays,
    link: "#",
    linkText: "Learn more",
    title: "Developmental Delays",
    whatItLooksLike:
      "Milestones like crawling, dressing, or play skills arrive later than peers.",
    howWeSupport:
      "We design OT and PT-informed routines that celebrate progress and build the foundational skills your child needs next.",
  },
  {
    imageSrc: emotionalRegulation,
    link: "#",
    linkText: "Learn more",
    title: "Emotional Regulation",
    whatItLooksLike:
      "Feelings go from zero to one hundred, and calming down feels out of reach.",
    howWeSupport:
      "Therapists teach body awareness, coping strategies, and mindfulness games that make emotional check-ins second nature.",
  },
  {
    imageSrc: feedingSleepToileting,
    link: "#",
    linkText: "Learn more",
    title: "Feeding, Sleep and Toileting",
    whatItLooksLike:
      "Meals become standoffs, bedtimes stretch forever, or potty training stalls.",
    howWeSupport:
      "We pair behavioral science with gentle routines so everyday caregiving moments feel manageable again.",
  },
  {
    imageSrc: parentingGuidance,
    link: "#",
    linkText: "Learn more",
    title: "Parenting Guidance & Support",
    whatItLooksLike:
      "You're juggling logistics, emotions, and endless decisions with little time for yourself.",
    howWeSupport:
      "Coaching sessions offer scripts, mindset shifts, and practical tools so you feel steady and supported.",
  },
  {
    imageSrc: socialInteraction,
    link: "#",
    linkText: "Learn more",
    title: "Social Interaction Difficulties",
    whatItLooksLike:
      "Playdates feel awkward, peers misread cues, or your child retreats when groups get loud.",
    howWeSupport:
      "Small-group practice, role-play, and social stories build confidence, perspective-taking, and authentic friendships.",
  },
];

function useViewportWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0,
  );

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return width;
}

const chunkArray = (items: SupportArea[], chunkSize: number) => {
  const chunks: SupportArea[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }
  return chunks;
};

export function AreasWeProvide() {
  const { t } = useTranslation(["common"]);
  const { direction, isArabic } = useLocal();
  const dirAttr: string | undefined = Array.isArray(direction)
    ? direction[0]
    : direction;

  const isRTL = dirAttr === "rtl";

  const viewportWidth = useViewportWidth();
  const isMobile = viewportWidth < 768;
  const [activeArea, setActiveArea] = useState<SupportArea | null>(null);

  const localizedAreas = useMemo(
    () =>
      supportAreas.map((area, i) => ({
        ...area,
        title: t(`supportAreas.${i}.rootTitle`),
        whatItLooksLike: t(`supportAreas.${i}.whatLooksLikeDescr`),
        howWeSupport: t(`supportAreas.${i}.howWeCanHelpDescr`),
        linkText: t(`areasWeProvide.cards.${i}.cta`),
      })),
    [t],
  );

  const mobileSlides = useMemo(
    () => chunkArray(localizedAreas, 6),
    [localizedAreas],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      slidesToScroll: 1,
      direction: isRTL ? "rtl" : "ltr",
    },
    [Autoplay({ delay: 3500, stopOnInteraction: false })],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!isMobile || !emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect, isMobile]);

  // Re-initialize embla when direction changes
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit({ direction: isRTL ? "rtl" : "ltr" });
  }, [isRTL, emblaApi]);

  useEffect(() => {
    if (!isMobile) setSelectedIndex(0);
  }, [isMobile]);

  const scrollTo = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  return (
    <section
      dir={dirAttr}
      className={`bg-[#F4F3FC] ${isArabic ? "font-ibmplex" : "font-inter"}`}
    >
      <div className="py-20 px-6 max-w-[1400px] mx-auto relative z-50">
        <div
          className="absolute inset-0 bg-no-repeat bg-center bg-contain z-10 mt-20"
          style={{
            backgroundImage: `url(${bgIcon})`,
            backgroundPosition: "left center",
            transform: isRTL ? "scaleX(-1)" : "none",
          }}
        />
        <div className="flex flex-col items-center gap-2 relative z-50">
          <p
            className={`text-[16px] font-bold text-[#4242CE] ${isArabic ? "font-ibmplex" : "font-funnel"} text-center`}
          >
            {t("areasWeProvide.areasHeader")}
          </p>
          <div className="bg-[#4242CE] h-[2px] w-[200px] mt-1" />
        </div>
        <p
          className={`relative z-50 text-center text-2xl md:text-[32px] font-bold text-gray ${isArabic ? "mt-7 font-ibmplex" : "mt-4 font-funnel"}`}
        >
          {t("areasWeProvide.mainTitle")}
        </p>

        {/* Mobile Carousel */}
        {isMobile ? (
          <div
            className="mt-10 overflow-hidden relative z-50"
            ref={emblaRef}
            dir={isRTL ? "rtl" : "ltr"}
          >
            <div className="flex -mx-2">
              {mobileSlides.map((slide, idx) => (
                <div key={`slide-${idx}`} className="flex-none w-full px-2">
                  <div className="grid grid-cols-2 gap-[10px]">
                    {slide.map((area) => (
                      <GuidedCareCard
                        key={area.title}
                        imageSrc={area.imageSrc}
                        title={area.title}
                        linkText={area.linkText}
                        linkHref={area.link}
                        alt={area.title}
                        onSelect={() => setActiveArea(area)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-2 mt-6">
              {scrollSnaps.map((_, idx) => (
                <button
                  key={`dot-${idx}`}
                  onClick={() => scrollTo(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                  className={`rounded-full transition-all duration-300 ${
                    idx === selectedIndex
                      ? "bg-[#000] w-3 h-3"
                      : "bg-gray-300 w-2.5 h-2.5"
                  }`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-[26px] relative z-50">
            {localizedAreas.map((area) => (
              <GuidedCareCard
                key={area.title}
                imageSrc={area.imageSrc}
                title={area.title}
                linkText={area.linkText}
                linkHref={area.link}
                alt={area.title}
                onSelect={() => setActiveArea(area)}
                isArabic={isArabic}
              />
            ))}
          </div>
        )}

        {activeArea && (
          <AreaDetailsModal
            area={activeArea}
            onClose={() => setActiveArea(null)}
            isRTL={isRTL}
          />
        )}
      </div>
    </section>
  );
}

function AreaDetailsModal({
  area,
  onClose,
  isRTL,
}: {
  area: SupportArea;
  onClose: () => void;
  isRTL?: boolean;
}) {
  const { t } = useTranslation(["common"]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <article className="relative w-full max-w-[360px] md:max-w-[640px] rounded-2xl bg-white p-6 md:p-8 shadow-xl border border-[#ECECF9]">
        <button
          aria-label="Close"
          className={`absolute top-4 ${isRTL ? "left-4" : "right-4"} text-gray-400 hover:text-gray-600`}
          onClick={onClose}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11 1L1 11M1 1L11 11"
              stroke="#7D7D7D"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div className="flex items-center gap-4">
          <div className="w-[60px] h-[60px] bg-[#F4F3FC] rounded-full px-[16px] py-[10px]">
            <img
              src={area.imageSrc}
              alt={area.title}
              className="w-[28px] h-[40px] m-auto"
            />
          </div>
          <p className="text-lg md:text-xl font-bold text-[#4242CE]">
            {area.title}
          </p>
        </div>
        <div className="mt-6 space-y-5 text-[#4B4B4B]">
          <div>
            <p className="font-semibold text-sm md:text-[16px] text-[#4C4C4C]">
              {t("whatLooksLike")}
            </p>
            <p className="md:text-sm mt-1 leading-5 text-[#7D7D7D]">
              {area.whatItLooksLike}
            </p>
          </div>
          <div>
            <p className="font-semibold text-sm md:text-[16px] text-[#4C4C4C]">
              {t("howWeSupport")}
            </p>
            <p className="md:text-sm mt-1 leading-5 text-[#7D7D7D]">
              {area.howWeSupport}
            </p>
          </div>
        </div>
      </article>
    </div>
  );
}
